import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  LearnerInviteListResponse,
  LearnerInviteRequest,
  LearnerInviteResponse,
  ResendStaffInviteResponse,
  RevokeStaffInviteResponse,
} from "@eduviaos/shared";
import { api } from "@/lib/api";
import { studentKeys } from "@/hooks/useStudents";

/**
 * Phase SC-4c — learner portal invites, driven from the roster.
 *
 * `POST /me/learner-invites` takes a `student_id`, not an email: the admin
 * picks a learner who already exists so one human keeps one roster row.
 * Redeeming binds `Student.user_id`, which is what the portal needs to
 * answer "which seat is mine".
 *
 * Revoke and resend deliberately reuse the staff-invite routes — a learner
 * invite *is* a `StaffInvite` document, and the BE picks the portal accept
 * URL for it — so there's no second set of endpoints to keep in step.
 *
 * Every mutation invalidates the students list as well as the invite list:
 * accepting an invite sets `Student.user_id`, so the roster's portal column
 * would otherwise keep saying "no access" until a manual refresh.
 */

export const learnerInviteKeys = {
  all: ["learner-invites"] as const,
  list: () => [...learnerInviteKeys.all, "list"] as const,
};

export function useLearnerInvites(enabled = true) {
  return useQuery<LearnerInviteListResponse>({
    queryKey: learnerInviteKeys.list(),
    queryFn: () => api.get<LearnerInviteListResponse>("/me/learner-invites"),
    enabled,
    staleTime: 30 * 1000,
  });
}

export function useInviteLearner() {
  const qc = useQueryClient();
  return useMutation<LearnerInviteResponse, Error, LearnerInviteRequest>({
    mutationKey: ["learner-invites", "create"],
    mutationFn: (body) => api.post<LearnerInviteResponse>("/me/learner-invites", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: learnerInviteKeys.all });
      // The invite may have written an email back onto the roster row.
      void qc.invalidateQueries({ queryKey: studentKeys.all });
    },
  });
}

export function useRevokeLearnerInvite() {
  const qc = useQueryClient();
  return useMutation<RevokeStaffInviteResponse, Error, string>({
    mutationKey: ["learner-invites", "revoke"],
    mutationFn: (inviteId) =>
      api.post<RevokeStaffInviteResponse>(`/me/first-run/staff-invites/${inviteId}/revoke`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: learnerInviteKeys.all });
    },
  });
}

export function useResendLearnerInvite() {
  const qc = useQueryClient();
  return useMutation<ResendStaffInviteResponse, Error, string>({
    mutationKey: ["learner-invites", "resend"],
    mutationFn: (inviteId) =>
      api.post<ResendStaffInviteResponse>(`/me/first-run/staff-invites/${inviteId}/resend`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: learnerInviteKeys.all });
    },
  });
}
